import { useApi } from '@/api/useApi'
import API from '@/api/apiUrls'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import type { Calculation } from '@/stores/type/calculation.type'
import { computed, ref } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'
import type { CalculationProgressInfo } from '@/components/calculation-result/type/type'
import { useDialog } from '@haskoning/vue-basestore/dist/composables/dialog'
import { useSelectedItemMixin } from '@/stores/useSelectedItemMixin'

const { api } = useApi()

const baseUrl = API.CORE.CALCULATIONS.INDEX
const checkProjectParamsUrl = API.CORE.CALCULATIONS.CHECK_PROJECT_PARAMS
const updateProjectParamsUrl = API.CORE.CALCULATIONS.UPDATE_PROJECT_PARAMS
const duplicateURL = API.CORE.CALCULATIONS.DUPLICATE
const bulkDuplicateUrl = API.CORE.CALCULATIONS.BULK_DUPLICATE
const exportExcelUrl = API.CORE.CALCULATIONS.EXPORT_EXCEL
const exportErrorURL = API.CORE.CALCULATIONS.EXPORT_ERROR
const getCeleryCalculateUrl = API.CORE.CALCULATIONS.CELERY_CALCULATE
const getCalculationProgressStepsUrl = API.CORE.CALCULATIONS.GET_CALCULATION_PROGRESS_STEPS
const getCalculationProgressUrl = API.CORE.CALCULATIONS.GET_CALCULATION_PROGRESS

export const useCalculationStore = defineStore('calculation', () => {
  const {
    showDialog: updateProjectParamsDialog,
    openDialog: openUpdateProjectParamsDialog,
    closeDialog: closeUpdateProjectParamsDialog
  } = useDialog()

  const {
    showDialog: duplicateDialog,
    dialogItem: duplicateDialogItem,
    openDialog: openDuplicateDialog,
    closeDialog: closeDuplicateDialog
  } = useDialog()

  const authorizationStore = useAuthorizationStore()

  const canAddCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['add_calculation']
    })
  })

  const canChangeCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_calculation']
    })
  })

  const canDeleteCalculation = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_calculation']
    })
  })

  const baseStore = useBaseStore<Calculation>({ service: baseUrl, api })
  const selectedItemMixin = useSelectedItemMixin(baseStore)

  const isApprovedProjectParams = ref(false)
  const exportLoading = ref(false)
  const errorLoading = ref(false)

  const filterFields = ref(['name', 'location', 'client', 'description'])

  const fieldsFiltered = computed(() => {
    return baseStore.fieldsList.value.filter((field) =>
      filterFields.value.includes(field.parameter)
    )
  })

  const checkProjectParams = async (item: Calculation): Promise<boolean | null> => {
    baseStore.writeLoading.value = true
    try {
      return await api.service(checkProjectParamsUrl(item.id)).get()
    } catch (e) {
      baseStore.setSnackbarMessage('Failed to check project parameters', 'error')
      console.error(e)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const updateProjectParams = async (item: Calculation): Promise<void> => {
    baseStore.writeLoading.value = true
    try {
      await api.service(updateProjectParamsUrl(item.id)).post(item)
    } catch (e) {
      baseStore.setSnackbarMessage('Failed to update project parameters', 'error')
      console.error(e)
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const duplicate = async (item: Calculation) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api.service(duplicateURL(item.id)).post(item)
      if (Array.isArray(responseData)) {
        baseStore.addItems(responseData)
      } else {
        baseStore.addItems([responseData])
      }
      return responseData
    } catch (e) {
      baseStore.setSnackbarMessage('Failed to duplicate calculation', 'error')
      console.error(e)
      return null
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const bulkDuplicate = async (items: Calculation[]) => {
    try {
      baseStore.writeLoading.value = true
      const responseData = await api.service(bulkDuplicateUrl).post(items)
      if (Array.isArray(responseData) && responseData.length > 0) {
        baseStore.addItems(responseData)
      }
    } catch (e) {
      baseStore.setSnackbarMessage('Failed to duplicate calculations', 'error')
      console.error(e)
    } finally {
      baseStore.writeLoading.value = false
    }
  }

  const createNewCalculation = (project: number) => {
    const newCalculation = {} as Calculation

    baseStore.fieldsList.value.forEach(({ parameter, children, default: defaultValue }) => {
      if (parameter === 'project') {
        newCalculation[parameter] = project
      } else if (children) {
        newCalculation[parameter] = Object.fromEntries(
          Object.entries(children).map(([subField, subFieldData]) => [
            subField,
            subFieldData.default
          ])
        )
      } else {
        newCalculation[parameter] = defaultValue
      }
    })
    newCalculation['vessel'] = []
    return newCalculation
  }

  const celeryCalculate = async (item: Calculation) => {
    try {
      return await api.service(getCeleryCalculateUrl(item.id)).post(item)
    } catch (e) {
      console.error(e)
      return null
    }
  }

  const getCalculationProgressSteps = async (id: number): Promise<[]> => {
    try {
      return await api.service(getCalculationProgressStepsUrl(id)).get()
    } catch (e) {
      console.error(e)
      return []
    }
  }

  const getCalculationProgress = async (id: number): Promise<CalculationProgressInfo | boolean> => {
    try {
      return await api.service(getCalculationProgressUrl(id)).get()
    } catch (e) {
      console.error(e)
      return false
    }
  }

  const moveSelectedItemsToArchive = () => {
    console.log('move-selected-items-to-archive')
  }

  const duplicateSelectedItems = () => {
    openDuplicateDialog()
  }

  const deleteSelectedItems = () => {
    baseStore.openDeleteDialog()
  }

  const toggleDuplicateDialog = (value: boolean | undefined): void => {
    duplicateDialog.value = value ?? !duplicateDialog.value
  }

  const downloadExcel = async (id: number) => {
    exportLoading.value = true
    try {
      await api.service(exportExcelUrl(id)).downloadBlob()
    } catch (e) {
      console.error(e)
    }
    exportLoading.value = false
  }

  const toggleIsApprovedProjectParams = () => {
    isApprovedProjectParams.value = !isApprovedProjectParams.value
  }

  const downloadErrorFile = async (id: number) => {
    errorLoading.value = true
    try {
      await api.service(exportErrorURL(id)).downloadBlob()
    } catch (e) {
      console.error(e)
    }
    errorLoading.value = false
  }

  function resetAll() {
    baseStore.reset()
  }

  return {
    ...baseStore,
    ...selectedItemMixin,
    exportLoading,
    isApprovedProjectParams,
    errorLoading,
    updateProjectParamsDialog,
    openUpdateProjectParamsDialog,
    closeUpdateProjectParamsDialog,
    duplicateDialog,
    duplicateDialogItem,
    openDuplicateDialog,
    closeDuplicateDialog,
    canAddCalculation,
    canChangeCalculation,
    canDeleteCalculation,
    fieldsFiltered,
    createNewCalculation,
    checkProjectParams,
    updateProjectParams,
    duplicate,
    bulkDuplicate,
    downloadExcel,
    downloadErrorFile,
    celeryCalculate,
    getCalculationProgressSteps,
    getCalculationProgress,
    toggleIsApprovedProjectParams,
    moveSelectedItemsToArchive,
    toggleDuplicateDialog,
    duplicateSelectedItems,
    deleteSelectedItems,
    resetAll
  }
})
